import React, { type ElementType } from 'react';

import {
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemProps,
  IconProps,
  SystemStyleObject,
  MenuProps,
  Portal,
  Button as ChakraButton,
} from '@chakra-ui/react';
import styled from '@emotion/styled';

import { ThreeDots } from 'assets/assetsV1/iconComponents';

type menuListProps = Array<
  MenuItemProps & {
    label?: string;
    Icon?: ElementType<IconProps>;
    onClick?: () => void;
  }
>;

interface Props {
  MenuIcon?: ElementType;
  menuList: menuListProps;
  sx?: SystemStyleObject;
  menuProps?: MenuProps;
  usePortal?: boolean;
}

const StyledMenuList = styled(MenuList)`
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 8px;
`;

const StyledMenuItem = styled(MenuItem)`
  &:hover {
    background: #e6f1fd;
  }
  &:focus {
    background: #e6f1fd;
  }
`;

const Menu: ElementType<Props> = ({
  MenuIcon = ThreeDots,
  menuList = [],
  sx = {},
  menuProps,
  usePortal = true,
}: Props) => {
  const ParentElement = usePortal ? Portal : React.Fragment;
  return (
    <ChakraMenu autoSelect={false} {...menuProps}>
      <MenuButton
        as={ChakraButton}
        marginRight="0px"
        leftIcon={<MenuIcon boxSize="24px" />}
        aria-label="Options"
        variant="tertiary"
        h="40px"
        w="40px"
        sx={{
          margin: '0px',
          padding: '0px',
          display: 'flex',
          '&[data-active]': {
            background: '#E6F1FD !important',
            svg: {
              color: '#1565BC !important',
            },
          },
          svg: {
            color: '#333333 !important',
          },
          _hover: {
            background: '#E6F1FD !important',
          },
          '& span': {
            margin: '0 auto',
            width: '100%',
            '& svg': {
              margin: '0 auto',
            },
          },
          ...sx,
        }}
      />
      <ParentElement>
        <StyledMenuList>
          {menuList.map((item, index) => {
            const { label, Icon, onClick } = item ?? {};
            const hasIcon = !!Icon;
            return (
              <StyledMenuItem icon={hasIcon && <Icon color="#666666" />} key={index} onClick={onClick} {...item}>
                {label}
              </StyledMenuItem>
            );
          })}
        </StyledMenuList>
      </ParentElement>
    </ChakraMenu>
  );
};

export { Menu };
